import { useState, useEffect, useLayoutEffect, useRef } from "react";

import { SUGGESTED_BUCKET_ID } from "/imports/ui/components/booking";

/**
 * Drives the mobile "all buckets in one scroll" layout: tracks which
 * time-of-day section is under the sticky bar (scroll-spy), measures the
 * sticky region so section anchors land just below it, and exposes a
 * tap-to-scroll helper that briefly suppresses the spy during the animation.
 * When `hasSuggested` is set, the "Suggested for you" block participates as a
 * section too (id SUGGESTED_BUCKET_ID) so the nav can highlight/scroll to it.
 *
 * Returns `sectionRefs` (the caller wires each section element into it) plus
 * `stickyRef` for the sticky region, the `activeBucket` id, the measured
 * `sectionOffset`, and `scrollToBucket`.
 */
export default function useLessonScrollSpy({ isMobile, bucketedSlots, hasSuggested = false }) {
  const [activeBucket, setActiveBucket] = useState(null);
  const [sectionOffset, setSectionOffset] = useState(220); // px from viewport top where slot sections begin

  const stickyRef = useRef(null); // sticky date strip + segments region
  const sectionRefs = useRef({}); // bucket id -> section element
  const isClickScrolling = useRef(false); // suppress spy while a tap-scroll animates
  const clickTimer = useRef(null);

  // Section ids in page order: the suggested block sits above the buckets.
  const sectionIds = (hasSuggested ? [SUGGESTED_BUCKET_ID] : []).concat(bucketedSlots.map((b) => b.id));
  const sectionIdsKey = sectionIds.join(",");

  // Keep the active segment valid as availability changes; default to the
  // topmost section that has slots.
  useEffect(() => {
    if (!sectionIds.length) {
      setActiveBucket(null);
      return;
    }
    if (!sectionIds.includes(activeBucket)) {
      setActiveBucket(sectionIds[0]);
    }
  }, [sectionIdsKey, activeBucket]);

  // Measure the sticky region so section anchors land just below it and the
  // scroll-spy detection band starts at the right place.
  useLayoutEffect(() => {
    if (!isMobile) return undefined;
    const measure = () => {
      const h = stickyRef.current?.offsetHeight || 0;
      setSectionOffset(64 + h + 8); // 64 = app bar height
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (stickyRef.current) ro.observe(stickyRef.current);
    return () => ro.disconnect();
  }, [isMobile, sectionIdsKey]);

  // Scroll-spy: highlight the segment for the bucket currently under the sticky
  // bar. Picks the last section whose top has scrolled up past a detection line
  // just below the sticky region. A fixed line is used instead of an
  // IntersectionObserver band because the band collapsed to zero height when the
  // expanded filter panel pushed sectionOffset past the viewport's mid-point,
  // which silently stopped the spy from updating on scroll.
  useEffect(() => {
    if (!isMobile || sectionIds.length === 0) return undefined;
    const onScroll = () => {
      if (isClickScrolling.current) return;
      const line = sectionOffset + 8;
      let next = sectionIds[0];
      for (const id of sectionIds) {
        const el = sectionRefs.current[id];
        if (!el) continue;
        if (el.getBoundingClientRect().top - line <= 1) next = id;
        else break;
      }
      // At the page bottom the last section may never cross the line; pin it.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
        next = sectionIds[sectionIds.length - 1];
      }
      setActiveBucket(next);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile, sectionIdsKey, sectionOffset]);

  const scrollToBucket = (id) => {
    setActiveBucket(id);
    const el = sectionRefs.current[id];
    if (!el) return;
    isClickScrolling.current = true;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 700);
  };

  return { stickyRef, sectionRefs, activeBucket, sectionOffset, scrollToBucket };
}
